import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePageVisibility, updatePageFromSocket } from '../../redux/Slices/pageSlice';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import './scss/PageVisibility.scss';

const PageVisibility = ({ page, workspaceId }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);

  // Determine if current user is owner
  const isOwner = useMemo(() => {
    if (!page || !user) return false;

    const userId = user?.user?._id;
    if (!userId) return false;

    // Check if user is page owner
    const pageMember = page.members?.find(m => {
      const memberId = m.user?._id || m.user;
      return memberId === userId;
    });
    return pageMember?.role === 'owner';
  }, [page, user]);

  // Get workspace name for description
  const workspaceDisplayName = useMemo(() => {
    return page?.workspace?.name || 'the Workspace';
  }, [page]);

  const visibilityOptions = useMemo(() => [
    {
      name: 'Private',
      description:
        'Only page members can see this page. Workspace admins can manage members.',
      icon: 'lock',
    },
    {
      name: 'Workspace',
      description:
        `All members of ${workspaceDisplayName} can see and edit this page.`,
      icon: 'group',
    },
    {
      name: 'Public',
      description:
        'Anyone on the internet can see this page. Only page members can edit.',
      icon: 'globe',
    },
  ], [workspaceDisplayName]);

  const handlePageVisibility = async (option) => {
    // Prevent action if disabled or already selected
    if (option.disabled || option.name === page.visibility || isUpdating) return;

    // Check if user has permission to change visibility
    if (!isOwner) {
      showError(dispatch, 'Only the page owner can change page visibility');
      return;
    }

    setIsUpdating(true);

    const originalVisibility = page.visibility;
    // Optimistic update
    dispatch(updatePageFromSocket({ ...page, visibility: option.name }));

    try {
      // Make API call
      await dispatch(updatePageVisibility({
        pageId: page.shortId,
        visibility: option.name
      })).unwrap();

      // Success feedback
      showSuccess(dispatch, `Page visibility changed to ${option.name}`);
      dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));

    } catch (error) {
      // Revert optimistic update
      dispatch(updatePageFromSocket({ ...page, visibility: originalVisibility }));
      const errorMessage = error.message || 'Failed to update page visibility';
      showError(dispatch, errorMessage);
      console.error('Error updating page visibility:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="change-visibility">
      <header className="change-visibility__header">
        <h3>Change visibility</h3>
        {!isOwner && (
          <p className="change-visibility__permission-notice">
            Only page owner can change visibility
          </p>
        )}
      </header>
      <ul className="change-visibility__options">
        {visibilityOptions.map((option) => {
          const isSelected = page.visibility === option.name;
          const isDisabled = !isOwner || isUpdating;

          return (
            <li
              key={option.name}
              className={`change-visibility__option ${isSelected ? 'selected' : ''}`}
              onClick={() => handlePageVisibility(option)}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            >
              <span className="material-symbols-outlined">{option.icon}</span>
              <div className="details">
                <h3>{option.name}</h3>
                <p>{option.description}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PageVisibility;

